import React from 'react';
import ReactDOM, { render } from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import ProductQuantityField from './Components/ProductQuantityField';
import ProductInfoButtons from './Components/ProductInfoButtonsContainer';
import ProductPrice from './Components/ProductPrice';
import LiveEditing from './Components/LiveEditing';
window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('BuyProductContainer')) {
        const element = document.getElementById('BuyProductContainer');
        renderReact(
            <Provider store={store}>
                <ProductQuantityField
                    minArticleQuantity={element.dataset.minArticleQuantity}
                    articleNumber={element.dataset.articleNumber}
                />
            </Provider>,
            document.getElementById('BuyProductContainer')
        );
    }
    if (document.getElementById('ProductInfoButtonsContainer')) {
        const element = document.getElementById('ProductInfoButtonsContainer');
        renderReact(
            <Provider store={store}>
                <ProductInfoButtons productId={element.dataset.productid} />
            </Provider>,
            element
        );
    }
    if (document.getElementById('quotationButton')) {
        var QuotationButton = DynamicComponent({
            loader: () => import('./Components/QuotationButtonContainer'),
        });

        const element = document.getElementById('quotationButton');
        renderReact(
            <Provider store={store}>
                <QuotationButton
                    productId={element.dataset.productId}
                    productName={element.dataset.productName}
                    quotationTerms={element.dataset.quotationTerms}
                />
            </Provider>,
            element
        );
    }
    if (document.getElementById('BuyProductPrice')) {
        const element = document.getElementById('BuyProductPrice');
        renderReact(
            <Provider store={store}>
                <ProductPrice
                    articleNumber={element.dataset.articleNumber}
                    prices={element.dataset.tierPrices}
                    currency={element.dataset.currency}
                />
            </Provider>,
            document.getElementById('BuyProductPrice')
        );
    }
    if (document.getElementById('newsletterBlock')) {
        var Newsletter = DynamicComponent({
            loader: () => import('./Components/Newsletter'),
        });

        renderReact(
            <Provider store={store}>
                <Newsletter />
            </Provider>,
            document.getElementById('newsletterBlock')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('PkSelect')) {
        var PkSelect = DynamicComponent({
            loader: () => import('./Components/PkSelect'),
        });

        const element = document.getElementById('PkSelect');
        const options = JSON.parse(element.getAttribute('data-options'));
        renderReact(
            <Provider store={store}>
                <PkSelect options={options} />
            </Provider>,
            element
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        const rootElement = document.getElementById('lightBoxImages');
        const flag = JSON.parse(rootElement.getAttribute('data-flag'));
        const emblem = JSON.parse(rootElement.getAttribute('data-emblem'));
        const images = Array.from(rootElement.querySelectorAll('template')).map(
            (img) => ({
                html: img.innerHTML,
                src: img.dataset.src,
            })
        );
        renderReact(
            <LightboxImages
                images={images}
                flag={flag}
                emblem={emblem}
                container={rootElement}
            />,
            rootElement
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('.category-swiper').length > 0) {
        const CategorySwiper = DynamicComponent({
            loader: () => import('./Components/CategorySwiper.js'),
        });

        Array.from(document.querySelectorAll('.category-swiper')).forEach(
            (slider) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );

                if (values.length > 0) {
                    renderReact(<CategorySwiper values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.querySelectorAll('wishlist-button').length > 0) {
        const WishlistButton = DynamicComponent({
            loader: () => import('./Components/WishlistButton.container'),
        });
        Array.from(document.querySelectorAll('wishlist-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                const includedInWishlistsArray = JSON.parse(
                    button.dataset.includedInWishlists
                );
                renderReact(
                    <Provider store={store}>
                        <WishlistButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                                includedInWishlistsArray,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('wishlist')) {
        const Wishlist = DynamicComponent({
            loader: () => import('./Components/Wishlist.container'),
        });
        renderReact(
            <Provider store={store}>
                <Wishlist />
            </Provider>,
            document.getElementById('wishlist')
        );
    }
    if (document.getElementById('wishlist-button-container')) {
        const WishlistAdd = DynamicComponent({
            loader: () => import('./Components/Wishlist.add'),
        });
        renderReact(
            <Provider store={store}>
                <WishlistAdd />
            </Provider>,
            document.getElementById('wishlist-button-add')
        );
    }

    if (
        document.getElementsByClassName('general-contact-form-button').length >
        0
    ) {
        const GeneralContactModal = DynamicComponent({
            loader: () => import('./Components/GeneralContactModal'),
        });
        Array.from(
            document.getElementsByClassName('general-contact-form-button')
        ).forEach((button) => {
            const descriptionData = JSON.parse(button.dataset.description);
            renderReact(
                <GeneralContactModal description={descriptionData.value} />,
                button
            );
        });
    }
};

bootstrapComponents();
